import React from 'react'
import { useQuery } from '@tanstack/react-query'

import {
  ActivitiesList,
  ContentContainer,
  ContentLabel,
  HeaderContainer,
  SkeletonList,
  Subtitle,
  Title
} from './styles'
import { Activity } from '../Activity'
import { SideModal, Skeleton } from '~/components/shared'
import { IBehavior } from '~/interfaces'
import { fetchActivitiesHistory, message } from '~/services'
import { transformDate } from '~/utils'

const Header = ({ subtitle }: { subtitle: string }): JSX.Element => (
  <HeaderContainer>
    <Title>{message('behaviors.activities')}</Title>
    <Subtitle>{subtitle}</Subtitle>
  </HeaderContainer>
)

interface IActivityDetail {
  statistics: string[]
  title: string
}

interface IContentProps {
  activitiesHistory?: IBehavior[]
  isLoading?: boolean
}

const Content = ({ activitiesHistory, isLoading }: IContentProps): JSX.Element => {
  const createActivityDetail = (activity: IBehavior) => {
    return activity.properties.reduce<IActivityDetail>(
      (newActivityDetail, property) => {
        if (property.type === 'Tipo de Exercício') newActivityDetail.title = property.value
        else
          newActivityDetail.statistics = [
            ...newActivityDetail.statistics,
            `${property.type}: ${property.value} ${property.unit}`
          ]

        return newActivityDetail
      },
      { statistics: [], title: '' }
    )
  }

  if (activitiesHistory?.length) {
    return (
      <ContentContainer>
        <ContentLabel>{message('behaviors.history')}</ContentLabel>
        <ActivitiesList>
          {activitiesHistory.map(activity => {
            const { title, statistics } = createActivityDetail(activity)

            return (
              <Activity
                key={activity.endDate}
                title={title}
                lastUpdate={transformDate({ date: activity.endDate, dateFormat: 'dd/MM - hh:mm aa' })}
                statistics={statistics}
              />
            )
          })}
        </ActivitiesList>
      </ContentContainer>
    )
  }

  if (isLoading) {
    return (
      <ContentContainer>
        <Skeleton height="16px" width="30%" />
        <SkeletonList />
      </ContentContainer>
    )
  }

  return (
    <ContentContainer>
      <ContentLabel>{message('behaviors.behaviour_unexpected_error')}</ContentLabel>
    </ContentContainer>
  )
}

interface IActivitiesHistoryProps {
  patientId: number
  show: boolean
  setShowModal: (isOpen: boolean) => void
}

export const ActivitiesHistory = ({ patientId, setShowModal, show }: IActivitiesHistoryProps): JSX.Element => {
  const { data: activitiesHistory, isLoading } = useQuery(
    ['activitiesHistory', patientId],
    async () => await fetchActivitiesHistory(patientId),
    { enabled: show }
  )

  const createSubtitle = (): string => {
    const isActivitiesHistoryEmpty = activitiesHistory === undefined || activitiesHistory.length < 1
    if (isActivitiesHistoryEmpty) return ''

    const firstActivityDate = activitiesHistory[0].endDate
    const lastActivityDate = activitiesHistory[activitiesHistory.length - 1].endDate
    return `${transformDate({ date: firstActivityDate })} - ${transformDate({ date: lastActivityDate })}`
  }

  return (
    <SideModal openSlideModal={show} setOpenSlideModal={setShowModal}>
      <Header subtitle={createSubtitle()} />
      <Content activitiesHistory={activitiesHistory} isLoading={isLoading} />
    </SideModal>
  )
}
